import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { OwnerBadge, OwnerFilter, HiddenMonitorsModal, fetchHidden } from './MonitorOwnership';

// ── Icons ──────────────────────────────────────────────────────────────────
var IconPlus    = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; };
var IconTrash   = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>; };
var IconSearch  = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; };
var IconRefresh = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>; };
var IconLock    = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>; };

// ── Status display helpers ─────────────────────────────────────────────────
var STATUS_META = {
  valid:    { label: 'Valid',    cls: 'status-match'   },
  expiring: { label: 'Expiring', cls: 'status-warning' },
  expired:  { label: 'Expired',  cls: 'status-down'    },
  error:    { label: 'Error',    cls: 'status-neutral' },
  pending:  { label: 'Pending',  cls: 'status-neutral' },
};

function StatusCell({ status }) {
  var meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={'status-badge ' + meta.cls} style={{ fontSize: '11px', padding: '2px 7px' }}>
      <span className="badge-dot" />
      {meta.label}
    </span>
  );
}

// Days remaining — green >30, amber ≤30, red ≤7 or negative
function daysColor(days) {
  if (days === null || days === undefined) return 'var(--text-muted)';
  if (days <= 7) return 'var(--danger)';
  if (days <= 30) return 'var(--warning)';
  return 'var(--success)';
}

function DaysCell({ days, status }) {
  if (days === null || days === undefined) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>-</span>;
  }
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700,
      color: daysColor(days),
    }}>
      {days < 0 ? days + 'd' : days + 'd'}
      <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 5 }}>
        {days < 0 ? 'overdue' : status === 'expiring' ? 'left' : 'left'}
      </span>
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  var d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function relTime(dateStr) {
  if (!dateStr) return 'never';
  var diff = Date.now() - new Date(dateStr).getTime();
  var mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
}

// ── Main component ─────────────────────────────────────────────────────────
function SSLMonitor({ token, user }) {
  var [monitors, setMonitors]         = useState([]);
  var [loading, setLoading]           = useState(true);
  var [showModal, setShowModal]       = useState(false);
  var [modalTab, setModalTab]         = useState('single');
  var [domain, setDomain]             = useState('');
  var [port, setPort]                 = useState('443');
  var [bulkText, setBulkText]         = useState('');
  var [submitting, setSubmitting]     = useState(false);
  var [importResult, setImportResult] = useState(null);
  var [error, setError]               = useState('');
  var [searchTerm, setSearchTerm]     = useState('');
  var [statusFilter, setStatusFilter] = useState(''); // '' | 'valid' | 'expiring' | 'problem'
  var [checkingId, setCheckingId]     = useState(null);
  var [checkingAll, setCheckingAll]   = useState(false);
  var [checkProgress, setCheckProgress] = useState({ current: 0, total: 0 });
  var [ownerFilter, setOwnerFilter]   = useState(''); // admin only: '' | 'default' | '<userId>'
  var [hidden, setHidden]             = useState([]);
  var [showHidden, setShowHidden]     = useState(false);

  var canEdit = user?.role === 'admin' || user?.role === 'editor';
  // Admin looking at another user's private monitors: view only.
  var viewingOtherUser = ownerFilter !== '' && ownerFilter !== 'default';

  var fetchMonitors = useCallback(async function() {
    try {
      var res = await api.get('/ssl', { params: ownerFilter ? { owner: ownerFilter } : {} });
      setMonitors(res.data);
      if (canEdit) setHidden(await fetchHidden('ssl'));
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch SSL monitors:', err);
      setLoading(false);
    }
  }, [ownerFilter, canEdit]);

  useEffect(function() {
    fetchMonitors();
    var interval = setInterval(fetchMonitors, 60000); // Refresh every 60 seconds
    return function() { clearInterval(interval); };
  }, [fetchMonitors]);

  var closeModal = function() {
    setShowModal(false);
    setError('');
    setImportResult(null);
  };

  var handleAddSingle = async function(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/ssl', { domain: domain, port: parseInt(port, 10) || 443 });
      setDomain('');
      setPort('443');
      setShowModal(false);
      fetchMonitors();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add domain');
    } finally {
      setSubmitting(false);
    }
  };

  var handleBulkAdd = async function(e) {
    e.preventDefault();
    setError('');
    setImportResult(null);
    var domains = bulkText.split('\n').map(function(d) { return d.trim(); }).filter(Boolean);
    if (domains.length === 0) { setError('Please enter at least one domain'); return; }

    setSubmitting(true);
    try {
      var res = await api.post('/ssl/bulk', { domains: domains });
      setImportResult(res.data);
      setBulkText('');
      fetchMonitors();
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Deletes a monitor, or for an editor on a default, removes it from their view.
  var handleDelete = async function(m) {
    var question = m.delete_action === 'hide'
      ? 'Delete "' + m.domain + '"? It will be removed from your account only.'
      : m.is_default
        ? 'Delete default SSL monitor ' + m.domain + '? It will be removed for everyone.'
        : 'Delete SSL monitor ' + m.domain + '?';
    if (!window.confirm(question)) return;
    try {
      await api.delete('/ssl/' + m.id);
      fetchMonitors();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete SSL monitor');
    }
  };

  var handleCheckOne = async function(id) {
    setCheckingId(id);
    try {
      await api.post('/ssl/' + id + '/check');
      await fetchMonitors();
    } catch (err) {
      alert('SSL check failed');
    } finally {
      setCheckingId(null);
    }
  };

  var handleCheckAll = async function() {
    setCheckingAll(true);
    var all = monitors.filter(function(m) { return m.can_check; });
    setCheckProgress({ current: 0, total: all.length });
    try {
      for (var i = 0; i < all.length; i++) {
        setCheckProgress({ current: i + 1, total: all.length });
        try {
          await api.post('/ssl/' + all[i].id + '/check');
        } catch (e) {}
        if ((i + 1) % 5 === 0 || i === all.length - 1) {
          await fetchMonitors();
        }
      }
    } finally {
      setCheckingAll(false);
      setCheckProgress({ current: 0, total: 0 });
      fetchMonitors();
    }
  };

  var filteredMonitors = monitors.filter(function(m) {
    var term = searchTerm.toLowerCase();
    var matchSearch = m.domain.toLowerCase().includes(term) ||
      (m.issued_to || '').toLowerCase().includes(term) ||
      (m.issuer || '').toLowerCase().includes(term);
    var matchStatus = true;
    if (statusFilter === 'valid')         matchStatus = m.status === 'valid';
    else if (statusFilter === 'expiring') matchStatus = m.status === 'expiring';
    else if (statusFilter === 'problem')  matchStatus = m.status === 'expired' || m.status === 'error';
    return matchSearch && matchStatus;
  });

  var validCount   = monitors.filter(function(m) { return m.status === 'valid'; }).length;
  var expiringCount= monitors.filter(function(m) { return m.status === 'expiring'; }).length;
  var problemCount = monitors.filter(function(m) { return m.status === 'expired' || m.status === 'error'; }).length;

  var progressPct = checkProgress.total > 0
    ? Math.round((checkProgress.current / checkProgress.total) * 100) : 0;

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading SSL certificates…</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>SSL Monitor</h1>
            <p>
              TLS certificate expiry for <strong style={{ color: 'var(--text-primary)' }}>{monitors.length}</strong> domain{monitors.length === 1 ? '' : 's'}
              {' '}— warns 30 days before expiry
            </p>
          </div>
          {canEdit && (
            <div className="page-actions">
              <button
                className="btn btn-primary"
                onClick={handleCheckAll}
                disabled={checkingAll || viewingOtherUser || monitors.length === 0}
                style={checkingAll ? {
                  background: 'linear-gradient(90deg, var(--primary) ' + progressPct + '%, transparent ' + progressPct + '%)',
                  border: '1px solid var(--primary)',
                  transition: 'all 0.3s ease',
                } : {}}
              >
                <IconRefresh /> {checkingAll ? checkProgress.current + '/' + checkProgress.total : 'Check All'}
              </button>
              <button className="btn btn-secondary" onClick={function() { setShowHidden(true); }}>
                Deleted ({hidden.length})
              </button>
              {!viewingOtherUser && (
                <button className="btn btn-primary" onClick={function() { setShowModal(true); setError(''); setImportResult(null); }}>
                  <IconPlus /> Add Domains
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {monitors.length > 0 && (
        <div className="cards-grid" style={{ marginBottom: 20, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {[
            { label: 'Total',          val: monitors.length, color: 'var(--info)',    filter: '' },
            { label: 'Valid',          val: validCount,      color: 'var(--success)', filter: 'valid' },
            { label: 'Expiring Soon',  val: expiringCount,   color: 'var(--warning)', filter: 'expiring' },
            { label: 'Expired / Error',val: problemCount,    color: 'var(--danger)',  filter: 'problem' },
          ].map(function(s) {
            return (
              <div key={s.label} className="card"
                   style={{ cursor: 'pointer', ...(statusFilter === s.filter && s.filter ? { borderColor: 'var(--primary)' } : {}) }}
                   onClick={function() { setStatusFilter(function(prev) { return prev === s.filter ? '' : s.filter; }); }}>
                <div className="card-title" style={{ marginBottom: 8 }}>{s.label}</div>
                <div className="card-value" style={{ fontSize: 26, color: s.color }}>{s.val}</div>
                {statusFilter === s.filter && s.filter && (
                  <div style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 600, marginTop: 4 }}>Filtering ×</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>Certificates</h3>
          <div className="table-toolbar">
            <OwnerFilter user={user} value={ownerFilter} onChange={setOwnerFilter} />
            <div className="search-input-wrap">
              <IconSearch />
              <input type="text" placeholder="Search domains…" value={searchTerm}
                     onChange={function(e) { setSearchTerm(e.target.value); }} />
            </div>
          </div>
        </div>

        {filteredMonitors.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconLock /></div>
            <div className="table-empty-title">{searchTerm || statusFilter ? 'No matching certificates' : 'No SSL monitors yet'}</div>
            <div className="table-empty-desc">{searchTerm || statusFilter ? 'Try a different search or clear the filter' : 'Add domains to start tracking certificate expiry'}</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Issued To</th>
                <th>Issuer</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Days Remaining</th>
                <th style={{ width: 130 }}>Expires On</th>
                {canEdit && <th style={{ width: 150 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredMonitors.map(function(m) {
                return (
                  <tr key={m.id}>
                    <td>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {m.domain}{m.port && m.port !== 443 ? ':' + m.port : ''}
                        <span style={{ marginLeft: 8, verticalAlign: 'middle' }}><OwnerBadge monitor={m} /></span>
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                        checked {relTime(m.last_checked)}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {m.issued_to || <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {m.issuer || <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </td>
                    <td>
                      <StatusCell status={m.status} />
                      {m.status === 'error' && m.error_message && (
                        <div style={{ fontSize: 10.5, color: 'var(--danger)', marginTop: 3, maxWidth: 220 }}
                             title={m.error_message}>
                          {m.error_message}
                        </div>
                      )}
                    </td>
                    <td><DaysCell days={m.days_remaining} status={m.status} /></td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {formatDate(m.valid_until)}
                    </td>
                    {canEdit && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {m.can_check && (
                            <button className="btn btn-secondary btn-sm" title="Re-check certificate"
                                    onClick={function() { handleCheckOne(m.id); }}>
                              <span style={checkingId === m.id
                                ? { display: 'inline-flex', animation: 'spin 1s linear infinite' }
                                : { display: 'inline-flex' }}>
                                <IconRefresh />
                              </span>
                            </button>
                          )}
                          {m.delete_action === 'delete' && (
                            <button className="btn btn-danger btn-sm" title="Delete"
                                    onClick={function() { handleDelete(m); }}>
                              <IconTrash />
                            </button>
                          )}
                          {m.delete_action === 'hide' && (
                            <button className="btn btn-danger btn-sm" title="Delete from your account only"
                                    onClick={function() { handleDelete(m); }}>
                              <IconTrash />
                            </button>
                          )}
                          {!m.can_check && !m.delete_action && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Read-only</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {filteredMonitors.length > 0 && (
          <div className="table-footer">
            Showing {filteredMonitors.length} of {monitors.length} certificates
            {statusFilter && (
              <button onClick={function() { setStatusFilter(''); }}
                style={{ marginLeft: 10, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {showHidden && (
        <HiddenMonitorsModal
          type="ssl"
          items={hidden}
          describe={function(m) { return m.domain + (m.port && m.port !== 443 ? ':' + m.port : ''); }}
          onClose={function() { setShowHidden(false); }}
          onRestored={fetchMonitors}
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 540 }} onClick={function(e) { e.stopPropagation(); }}>
            <div className="modal-header">
              <h3>Add SSL Monitor</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-table-head)', borderRadius: 'var(--radius-md)', padding: 4, marginBottom: 18 }}>
                {[['single', 'Single Domain'], ['bulk', 'Bulk Import']].map(function(item) {
                  var key = item[0]; var label = item[1];
                  return (
                    <button key={key} onClick={function() { setModalTab(key); setError(''); setImportResult(null); }}
                      style={{ flex: 1, padding: '6px 12px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
                        background: modalTab === key ? 'var(--bg-card)' : 'transparent',
                        color: modalTab === key ? 'var(--text-primary)' : 'var(--text-muted)',
                        boxShadow: modalTab === key ? 'var(--shadow-sm)' : 'none',
                        fontFamily: 'var(--font)', transition: 'all 0.15s ease' }}>
                      {label}
                    </button>
                  );
                })}
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-hint" style={{ marginBottom: 12 }}>
                {user?.role === 'admin'
                  ? 'Domains you add are defaults, visible to every user.'
                  : 'Domains you add are private: only you see them and get their alerts.'}
              </div>
              {importResult && (
                <div className="alert alert-success">
                  Added <strong>{importResult.added?.length || 0}</strong> domain(s).
                  {importResult.skipped?.length > 0 && ' ' + importResult.skipped.length + ' already existed.'}
                  {importResult.errors?.length > 0 && ' ' + importResult.errors.length + ' error(s).'}
                </div>
              )}

              {modalTab === 'single' ? (
                <form onSubmit={handleAddSingle}>
                  <div className="form-group">
                    <label>Domain <span className="label-hint">*required</span></label>
                    <input type="text" value={domain} onChange={function(e) { setDomain(e.target.value); }}
                           required placeholder="example.com" autoFocus />
                    <div className="form-hint">Paste a full URL if you like — <code>https://</code> and any path are stripped automatically.</div>
                  </div>
                  <div className="form-group">
                    <label>Port <span className="label-hint">optional</span></label>
                    <input type="number" value={port} onChange={function(e) { setPort(e.target.value); }}
                           placeholder="443" min="1" max="65535" />
                    <div className="form-hint">Defaults to 443. Use another port for services like IMAPS (993) or SMTPS (465).</div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Checking…' : 'Add & Check Certificate'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleBulkAdd}>
                  <div className="form-group">
                    <label>Domains <span className="label-hint">one per line</span></label>
                    <textarea value={bulkText} onChange={function(e) { setBulkText(e.target.value); }}
                              placeholder={'example.com\nhttps://another.com/page\nmail.example.org'}
                              rows={10} style={{ minHeight: 200 }} autoFocus />
                    <div className="form-hint">
                      {bulkText.split('\n').filter(function(d) { return d.trim(); }).length} domain(s) entered.
                      All use port 443. URLs are cleaned up automatically.
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Importing…' : 'Import All Domains'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SSLMonitor;
